import reduxStore from "../../configureEnvStore";
import { APIs } from "../../constants/APIConstants";
import { ENABLED } from "../../constants/Constants";
import { NOTIFICATION_TYPES } from "../../constants/DataTrackNotificationConstants";
import { CALL_DATA, GLOBAL_MEETING_DATA, PublicVariables } from "../../constants/PublicVariables";
import { MAX_IMAGE_COUNT_OF_INDEXED_DB, UPLOAD_TYPE_PARAM_FOR_UPLOAD_FILES } from "../../constants/UploadFilesConstants";
import Localize from "../../locale/Localize";
import { LogFirebase } from "../../services/firestore";
import takePhotoService from "../../services/takePhotoService";
import watermarkImageService from "../../services/WatermarkImage/watermarkImageService";
import { sendDataTrack } from "../../shared/dataTrackFunction";
import { isIOSOrMacSafariImageBlobIssue } from "../../shared/utility";
import CustomToast from "../UI/customToast";
import { indexedDBService } from "./indexedDBService";

class OfflineModeService {
    imageDataList = [];
    uploading = 0;
    uploaded = 0;
    isUploading = false;
    uploadFailedCount = 0;

    prepareOfflineFormData = (imageData) => {
        let blob = imageData.imgData;
        if (isIOSOrMacSafariImageBlobIssue()) {
            LogFirebase("OfflineMode", "prepareOfflineFormData_for_iOS", { base64_data: "true" });
            blob = watermarkImageService.__base64ToBlobImage.convertData(imageData.imgData);
        }
        const fileName = imageData.name;
        const fileObj = new File([blob], fileName, { type: "image/jpeg" });
        const formData = new FormData();
        formData.append("file", fileObj);
        const featureFlag = PublicVariables.featureFlag;
        formData.append(
            "folderName",
            featureFlag?.FOLDERNAMEFORMAT && featureFlag?.ENABLEFOLDERNAMING === ENABLED ? featureFlag.FOLDERNAMEFORMAT : ""
        );

        if (featureFlag?.WATERMARKONPHOTOS === ENABLED) {
            const latValue = imageData.lat;
            const longValue = imageData.long;
            if (latValue != undefined && latValue != null && latValue != "" && latValue != 0.0 && latValue != 0)
                formData.append("latitude", latValue);
            if (longValue != undefined && longValue != null && longValue != "" && longValue != 0.0 && longValue != 0)
                formData.append("longitude", longValue);
        }

        formData.append("fileName", fileName);
        formData.append("sessionId", imageData.sessionId);
        formData.append("fileType", "img");
        formData.append("fromGallery", UPLOAD_TYPE_PARAM_FOR_UPLOAD_FILES); // Shown Caption in Agent portal
        if (imageData.caption) {
            formData.append("caption", imageData.caption);
        }
        LogFirebase("OfflineMode", "prepareOfflineFormData_caption", {
            caption: imageData?.caption,
            fileName: fileName,
            latitude: imageData?.lat,
            longitude: imageData?.long,
        });
        return formData;
    };

    uploadImage(imageFor, imageData, callbackForUploadImage) {
        const formData = this.prepareOfflineFormData(imageData);
        const uploadFileUrl = APIs.UPLOAD_SESSION_FILE;
        takePhotoService.sendUploadImageRequest(uploadFileUrl, formData, imageFor, callbackForUploadImage);
    }

    initializeUploadStatus() {
        this.isUploading = true;
        this.uploading++;
        takePhotoService.showUploadingStatus(
            Localize[PublicVariables.currentLanguageObj.languageCode]["uploading"] + " (" + this.uploading + "/" + this.imageDataList.length + ")"
        );
    }

    handleUploadedPhotoInfo(result) {
        this.uploaded++;
        takePhotoService.showUploadingStatus(
            Localize[PublicVariables.currentLanguageObj.languageCode]["uploading"] + " (" + this.uploaded + "/" + this.imageDataList.length + ")"
        );
        if (GLOBAL_MEETING_DATA.isAgent && result) {
            LogFirebase("OfflineMode", "thumb_image_data_add", { URL: result?.fileUrl });
            takePhotoService.addThumbInList(result.thumbUrl, result.fileUrl, result.fileName, result.caption);
        }
    }

    sendTrackOnUploadFileStartEnd = (result) => {
        const msg = {
            notification_type: NOTIFICATION_TYPES.OFFLINE_MODE_UPLOAD_FILE,
            device_type: GLOBAL_MEETING_DATA.device_type,
            status: "2",
        };
        if (CALL_DATA.localIdentity) {
            msg.identity = CALL_DATA.localIdentity;
        }
        msg.image_url = result.fileUrl;
        msg.caption = result.caption;
        msg.thumbURL = result.thumbUrl;
        msg.imageName = result.fileName;

        sendDataTrack(msg);
    };

    handleUploadPhotoCompleted(result) {
        //send acknowledge to agent for photo upload status
        if (!GLOBAL_MEETING_DATA.isAgent && result) {
            this.sendTrackOnUploadFileStartEnd(result);
        }
        if (this.uploaded == this.imageDataList.length) {
            LogFirebase("OfflineMode", "all_image_upload_complate", { image_list_length: this.imageDataList?.length });
            this.uploading = 0;
            this.uploaded = 0;
            this.imageDataList = [];
            setTimeout(() => {
                if (this.uploadFailedCount <= 0) {
                    LogFirebase("OfflineMode", "all_image_upload_complate_if", { uploadFailedCount: this.uploadFailedCount });
                    const successToastMsg = Localize[PublicVariables.currentLanguageObj.languageCode]["successfully_uploaded"];
                    takePhotoService.showUploadingStatus(successToastMsg, true);
                    const { isRoomConnected } = reduxStore.getState().commonReducer;
                    if (!isRoomConnected) {
                        CustomToast.success(successToastMsg); // show toast message if call is disconnected
                    }
                } else {
                    LogFirebase("OfflineMode", "all_image_upload_complate_else", { uploadFailedCount: this.uploadFailedCount });
                    this.uploadFailedCount = 0;
                    takePhotoService.dismissToastObj();
                }
                this.isUploading = false;
            }, 1000);
        }
    }

    uploadPhoto = (imageData, retries = 3) => {
        return new Promise((resolve, reject) => {
            const handleSuccess = (response, result, attempt) => {
                LogFirebase("OfflineMode", "images_upload_success", {
                    URL: result?.fileUrl,
                    image_name: result?.fileName,
                });
                this.handleUploadedPhotoInfo(result);
                this.handleUploadPhotoCompleted(result, true);
                resolve(response);
            };

            const handleFailure = (attempt) => {
                LogFirebase("OfflineMode", "images_upload_failed", { attempt });
                this.uploadFailedCount++;
                this.handleUploadedPhotoInfo(null);
                this.handleUploadPhotoCompleted(null);
                reject(new Error("Upload failed after 3 attempts"));
            };

            const handleRetry = (attempt) => {
                LogFirebase("OfflineMode", "images_upload_retry", { attempt });
                setTimeout(() => attemptUpload(attempt + 1), 500);
            };

            const handleResponse = (response, attempt) => {
                LogFirebase("OfflineMode", "images_upload_complete", {
                    success: response?.success,
                    attempt,
                });

                const result = response?.data;
                if (response?.success > 0) {
                    handleSuccess(response, result, attempt);
                } else if (attempt < retries - 1) {
                    handleRetry(attempt);
                } else {
                    handleFailure(attempt);
                }
            };

            const attemptUpload = (attempt) => {
                this.uploadImage("OfflineMode", imageData, (response) => handleResponse(response, attempt));
            };

            attemptUpload(0); // Start first attempt
        });
    };

    handleUploadFileDataTrackMessage = (msg) => {
        if (msg.status === "2") {
            if (GLOBAL_MEETING_DATA.ucid) {
                LogFirebase("OfflineMode", "thumb_image_data_receive", { URL: msg?.image_url });
                takePhotoService.addThumbInList(msg.thumbURL, msg.image_url, msg.imageName, msg.caption);
            }
        }
    };

    checkIndexedDBImagesLimitExceed = async () => {
        const images = await indexedDBService.getAllImages();
        LogFirebase("OfflineMode", "indexed_DB_store_image_count_checkIndexedDBImagesLimitExceed", { count: images?.length });
        if (images?.length > MAX_IMAGE_COUNT_OF_INDEXED_DB) {
            return true;
        } else {
            return false;
        }
    };

    resetofflineUploadVariables() {
        this.imageDataList = [];
        this.uploading = 0;
        this.uploaded = 0;
        this.isUploading = false;
        this.uploadFailedCount = 0;
    }
}
export const offlineModeService = new OfflineModeService();
